import requests, { setToken } from "./httpServices";
import { showErrorAlert } from "./AlertService";

const AdminServices = {
  getVisitorAnalytics: async (params) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.get("/admin/visitor-analytics", { params });
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  getAllSchools: async (params) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);
      const response = await requests.get("/admin/get-all-schools", { params });
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  getAllTeachers: async (params) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);
      const response = await requests.get("/admin/get-all-teachers", {
        params,
      });
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  // Logged-in school's own subscription status (drives the nav Renew nudge).
  // Non-fatal: returns null on error so the nav simply omits the nudge.
  /**
   * The caller cannot tell "this school has no subscription" from "the request
   * failed" if both come back null, and the renewal page rendered the same
   * "we couldn't find an active subscription" notice either way. A school whose
   * session had timed out was told its subscription did not exist.
   *
   * Failures now carry a shape of their own, so the page can say what actually
   * happened.
   */
  getMySubscription: async () => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      return await requests.get("/admin/my-subscription");
    } catch (error) {
      const status = error?.response?.status || null;
      return { error: true, status, hasSubscription: false };
    }
  },
  getDashboardStats: async () => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      console.log("📊 Calling dashboard stats API...");
      const response = await requests.get("/admin/dashboard-stats");
      console.log("📊 Dashboard stats API response:", response);
      return response;
    } catch (error) {
      console.error("❌ Dashboard stats API error:", error);
      console.error("Error details:", {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        url: error?.config?.url
      });
      // Don't show error alert for dashboard stats - let it fail gracefully
      // showErrorAlert(error);
      throw error;
    }
  },
  getSchoolTeachers: async (param) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);
      const response = await requests.get(
        "/admin/get-all-school-teachers?school_id=" + param.school_id,
        { param }
      );
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  getAllInfluences: async () => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);
      const response = await requests.get("/admin/get-all-influencers");
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  getAllGrantApplications: async () => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);
      const response = await requests.get("/admin/get-all-grant-applications");
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  changeAdminPassword: async (changeData) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);

      const response = await requests.post("/admin/change-password", changeData);
      if (response.success) return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  updateTeacher: async (teacherData) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.put(`/admin/update-teacher`, teacherData);
      return response;
    } catch (error) {
      showErrorAlert(error);
    }
  },
  updateSchool: async (schoolData) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.put(`/admin/update-school`, schoolData);
      return response;
    } catch (error) {
      showErrorAlert(error);
    }
  },
  // Reclassify a school between dashboard tabs. Lets errors propagate so the caller can toast.
  setCustomerStatus: async (schoolId, customerStatus) => {
    const token = localStorage.getItem("token");
    setToken(token);
    const response = await requests.put(`/admin/set-customer-status`, { schoolId, customerStatus });
    return response;
  },
  deleteSchool: async (schoolId) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);

      const response = await requests.delete(
        `/admin/delete-school/${schoolId}`
      );

      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  deleteGrantApplication: async (grantId) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);

      const response = await requests.delete(
        `/admin/delete-grant-application/${grantId}`
      );

      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  resetUserPassword: async (resetData) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);

      const response = await requests.post("/admin/admin-reset-password", resetData);
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  getTeachersQuizStatus: async () => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.get("/admin/teachers-quiz-status");
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  getSchoolPaymentStatus: async (schoolId) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);

      if (!schoolId) {
        throw new Error("School ID is required");
      }

      console.log(`[getSchoolPaymentStatus] Fetching payment status for school ${schoolId}`);

      const response = await requests.get(`/school/payment-status/${schoolId}`);

      console.log(`[getSchoolPaymentStatus] Status retrieved:`, response);

      return response?.status || response;
    } catch (error) {
      console.error("Error getting school payment status:", error);
      showErrorAlert(error);
      throw error;
    }
  },
};

export default AdminServices;
